import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import CambioPasswordDialog from '@/components/CambioPasswordDialog';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Popover, PopoverContent, PopoverTrigger
} from '@/components/ui/popover';
import GlobalSearch from '@/components/GlobalSearch';
import SedeSelector from '@/components/SedeSelector';
import QRScanner from '@/components/QRScanner';
import {
  LayoutDashboard, Users, Pill, FileText, AlertTriangle,
  BarChart3, Settings, LogOut, ChevronRight, Menu, X, Activity, Bell, Clock, Target, Zap,
  FileSpreadsheet, ClipboardCheck, Package, ClipboardList, CalendarCheck, UserCog, Wrench, Download, CalendarClock, HeartPulse, FlaskConical, Scale, ShieldAlert, ShieldCheck, Inbox, ScanSearch, Gauge
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, roles: null },
  { path: '/ospiti', label: 'Ospiti', icon: Users, roles: null },
  { path: '/terapie', label: 'Terapie', icon: Pill, roles: ['medico', 'infermiere', 'coordinatore'] },
  { path: '/controllo-aifa', label: 'Controllo AIFA', icon: ScanSearch, roles: ['medico', 'infermiere', 'coordinatore'] },
  { path: '/somministrazioni', label: 'Somministrazioni', icon: ClipboardCheck, roles: ['coordinatore', 'infermiere', 'medico', 'oss'] },
  { path: '/diario', label: 'Diario Clinico', icon: FileText, roles: null },
  { path: '/eventi', label: 'Eventi Avversi', icon: AlertTriangle, roles: null },
  { path: '/scale-alert', label: 'Scale e Alert Clinici', icon: ShieldAlert, roles: ['coordinatore', 'infermiere', 'medico'] },
  { path: '/qualita', label: 'Cruscotto Qualita', icon: ShieldCheck, roles: ['coordinatore', 'amministrazione', 'medico'] },
  { path: '/direzione', label: 'Cruscotto Direzionale', icon: Gauge, roles: ['coordinatore', 'amministrazione'] },
  { path: '/preventivi', label: 'Ammissioni', icon: FileSpreadsheet, roles: ['coordinatore', 'amministrazione', 'medico'] },
  { path: '/ordini-farmaci', label: 'Ordini Farmaci', icon: Package, roles: ['coordinatore', 'infermiere', 'medico'] },
  { path: '/appuntamenti', label: 'Appuntamenti', icon: CalendarCheck, roles: ['coordinatore', 'infermiere', 'medico', 'oss'] },
  { path: '/consegna-turno', label: 'Consegna Turno', icon: ClipboardList, roles: ['coordinatore', 'infermiere', 'medico', 'oss'] },
  { path: '/moduli', label: 'Moduli Operativi', icon: ClipboardCheck, roles: ['coordinatore', 'infermiere', 'medico', 'oss'] },
  { path: '/scorte-valorizza', label: 'Scorte da Valorizzare', icon: Package, roles: ['coordinatore', 'infermiere'] },
  { path: '/anagrafiche-da-completare', label: 'Anagrafiche Incomplete', icon: UserCog, roles: ['coordinatore', 'amministrazione', 'infermiere'] },
  { path: '/laboratorio', label: 'Laboratorio', icon: FlaskConical, roles: ['coordinatore', 'medico', 'infermiere', 'amministrazione'] },
  { path: '/smistamento', label: 'Documenti da smistare', icon: Inbox, roles: ['coordinatore', 'medico', 'infermiere', 'amministrazione'] },
  { path: '/scadenze', label: 'Scadenzario', icon: CalendarClock, roles: ['coordinatore', 'infermiere', 'medico'] },
  { path: '/report', label: 'Report', icon: BarChart3, roles: ['coordinatore', 'amministrazione', 'medico'] },
  { path: '/mmg', label: 'Medici MMG', icon: Activity, roles: ['coordinatore', 'medico'] },
  { path: '/csv', label: 'Import/Export', icon: Download, roles: ['coordinatore', 'amministrazione'] },
  { path: '/utenti', label: 'Gestione Utenti', icon: UserCog, roles: ['coordinatore'] },
  { path: '/impostazioni', label: 'Impostazioni', icon: Settings, roles: ['coordinatore'] },
];

const roleLabels = {
  coordinatore: 'Coordinatore',
  medico: 'Medico',
  infermiere: 'Infermiere',
  oss: 'OSS',
  amministrazione: 'Amministrazione',
};

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifiche, setNotifiche] = useState({ notifiche: [], totale: 0 });
  const [notifOpen, setNotifOpen] = useState(false);
  const [daSmistare, setDaSmistare] = useState(0);

  const fetchNotifiche = useCallback(() => {
    api.get('/report/notifiche').then(r => setNotifiche(r.data)).catch(() => {});
    api.get('/acquisizione/conteggio').then(r => setDaSmistare(r.data.da_smistare || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchNotifiche();
    const interval = setInterval(fetchNotifiche, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifiche]);

  const filteredNav = navItems.filter(item =>
    !item.roles || item.roles.includes(user?.role)
  );

  const farmaciBadge = notifiche.farmaci_esaurimento || 0;

  const initials = user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?';

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar fixed lg:static inset-y-0 left-0 z-50 w-64 flex flex-col transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        style={{ backgroundColor: '#04291a', color: 'hsl(150 30% 96%)' }}
        data-testid="sidebar">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
          <img
            src="https://customer-assets.emergentagent.com/job_resident-health-hub/artifacts/scmtxjkj_IMG_0881.jpeg"
            alt="Santa Maria Valleverde"
            className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
          />
          <div className="min-w-0">
            <h1 className="text-sm font-bold tracking-tight font-[Cinzel] leading-tight truncate">S. Maria Valleverde</h1>
            <p className="text-[9px] uppercase tracking-widest text-slate-400 leading-tight">Residenza Socio-Assistenziale</p>
          </div>
          <button className="lg:hidden ml-auto p-1" onClick={() => setMobileOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {filteredNav.map(item => {
            const Icon = item.icon;
            const isActive = item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group ${
                  isActive
                    ? 'bg-brand text-white shadow-lg shadow-brand/30'
                    : 'text-emerald-100/60 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={isActive ? 2 : 1.5} />
                <span className="flex-1">{item.label}</span>
                {item.path === '/ordini-farmaci' && farmaciBadge > 0 && (
                  <span className="flex items-center justify-center w-5 h-5 text-[9px] font-bold text-white bg-red-500 rounded-full">{farmaciBadge > 9 ? '9+' : farmaciBadge}</span>
                )}
                {item.path === '/smistamento' && daSmistare > 0 && (
                  <span className="flex items-center justify-center min-w-5 h-5 px-1 text-[9px] font-bold text-white bg-amber-500 rounded-full" data-testid="badge-smistamento">{daSmistare > 9 ? '9+' : daSmistare}</span>
                )}
                {isActive && <ChevronRight className="w-4 h-4 ml-auto opacity-60" />}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-4 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2">
            <Avatar className="w-8 h-8">
              <AvatarFallback className="bg-brand text-white text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{user?.full_name}</p>
              <p className="text-[10px] text-slate-400">{roleLabels[user?.role]}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-14 border-b border-slate-200 bg-white/80 backdrop-blur-md flex items-center justify-between px-3 sm:px-6 flex-shrink-0 no-print gap-2" data-testid="top-header">
          <button className="lg:hidden p-2 -ml-2 rounded-md hover:bg-slate-100" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5 text-slate-600" />
          </button>
          <GlobalSearch />

          <div className="flex items-center gap-2">
            <SedeSelector />
            <QRScanner buttonLabel="Scan QR" buttonSize="sm" className="text-slate-600 border-slate-200" testId="global-qr-scanner" />
            {/* Notification Bell */}
            <Popover open={notifOpen} onOpenChange={setNotifOpen}>
              <PopoverTrigger asChild>
                <button className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="notification-bell">
                  <Bell className="w-5 h-5 text-slate-600" />
                  {notifiche.totale > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full animate-pulse">
                      {notifiche.totale > 9 ? '9+' : notifiche.totale}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-96 p-0" data-testid="notification-panel">
                <div className="px-4 py-3 border-b bg-slate-50">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-800">Notifiche</h3>
                    <div className="flex gap-1.5">
                      {notifiche.terapie_mancanti > 0 && <Badge className="bg-red-100 text-red-700 text-[10px]"><Pill className="w-3 h-3 mr-0.5" />{notifiche.terapie_mancanti}</Badge>}
                      {notifiche.pai_scadenza > 0 && <Badge className="bg-amber-100 text-amber-700 text-[10px]"><Target className="w-3 h-3 mr-0.5" />{notifiche.pai_scadenza}</Badge>}
                      {notifiche.eventi_recenti > 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px]"><Zap className="w-3 h-3 mr-0.5" />{notifiche.eventi_recenti}</Badge>}
                      {notifiche.farmaci_esaurimento > 0 && <Badge className="bg-orange-100 text-orange-700 text-[10px]"><Package className="w-3 h-3 mr-0.5" />{notifiche.farmaci_esaurimento}</Badge>}
                      {notifiche.appuntamenti_imminenti > 0 && <Badge className="bg-cyan-100 text-cyan-700 text-[10px]"><CalendarCheck className="w-3 h-3 mr-0.5" />{notifiche.appuntamenti_imminenti}</Badge>}
                      {notifiche.terapie_scadenza > 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px]"><Clock className="w-3 h-3 mr-0.5" />{notifiche.terapie_scadenza}</Badge>}
                      {notifiche.ecg_alert > 0 && <Badge className="bg-rose-100 text-rose-700 text-[10px]"><HeartPulse className="w-3 h-3 mr-0.5" />{notifiche.ecg_alert}</Badge>}
                      {notifiche.referti_nuovi > 0 && <Badge className="bg-brand-soft text-brand-dark text-[10px]"><FlaskConical className="w-3 h-3 mr-0.5" />{notifiche.referti_nuovi}</Badge>}
                      {notifiche.peso_alert > 0 && <Badge className="bg-fuchsia-100 text-fuchsia-700 text-[10px]"><Scale className="w-3 h-3 mr-0.5" />{notifiche.peso_alert}</Badge>}
                    </div>
                  </div>
                </div>
                <ScrollArea className="max-h-80">
                  {notifiche.notifiche?.length === 0 ? (
                    <div className="py-8 text-center">
                      <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-xs text-slate-400">Nessuna notifica</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {notifiche.notifiche?.map((n, i) => {
                        const iconMap = { terapia_mancante: Pill, pai_scadenza: Target, pai_obiettivo_scadenza: Target, pai_scaduto: Target, terapia_scadenza: Clock, appuntamento_imminente: CalendarCheck, evento_recente: AlertTriangle, farmaco_esaurimento: Package, ecg_alert: HeartPulse, ecg_controllo: HeartPulse, referto_nuovo: FlaskConical, referto_da_assegnare: FlaskConical, peso_alert: Scale, alvo_alert: ClipboardList, doccia_mancante: ClipboardCheck };
                        const colorMap = { terapia_mancante: 'text-red-500 bg-red-50', pai_scadenza: 'text-amber-500 bg-amber-50', pai_obiettivo_scadenza: 'text-amber-500 bg-amber-50', pai_scaduto: 'text-red-500 bg-red-50', terapia_scadenza: 'text-blue-500 bg-blue-50', appuntamento_imminente: 'text-cyan-500 bg-cyan-50', evento_recente: 'text-blue-500 bg-blue-50', farmaco_esaurimento: 'text-orange-500 bg-orange-50', ecg_alert: 'text-rose-500 bg-rose-50', ecg_controllo: 'text-rose-500 bg-rose-50', referto_nuovo: 'text-brand bg-brand-soft', referto_da_assegnare: 'text-amber-600 bg-amber-50', peso_alert: 'text-fuchsia-500 bg-fuchsia-50', alvo_alert: 'text-amber-600 bg-amber-50', doccia_mancante: 'text-sky-500 bg-sky-50' };
                        const Icon = iconMap[n.tipo] || Bell;
                        const color = colorMap[n.tipo] || 'text-slate-500 bg-slate-50';
                        return (
                          <button
                            key={i}
                            className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                            data-testid={`notifica-${i}`}
                            onClick={() => { if (n.ospite_id) { navigate(`/ospiti/${n.ospite_id}`); setNotifOpen(false); } }}
                          >
                            <div className={`p-1.5 rounded-md flex-shrink-0 mt-0.5 ${color}`}><Icon className="w-3.5 h-3.5" /></div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-slate-800 leading-relaxed">{n.messaggio}</p>
                              {n.stanza && <p className="text-[10px] text-slate-400 mt-0.5">Camera {n.stanza}</p>}
                            </div>
                            <Badge className={`text-[9px] flex-shrink-0 ${n.priorita === 'alta' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{n.priorita}</Badge>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>

          <DropdownMenu>
            <DropdownMenuTrigger asChild data-testid="user-menu-trigger">
              <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                <Avatar className="w-7 h-7">
                  <AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-semibold">{initials}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium text-slate-700 hidden sm:block">{user?.full_name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.full_name}</p>
                <p className="text-xs text-slate-500">{roleLabels[user?.role]}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-3 sm:p-6" data-testid="main-content">
          <Outlet />
          <CambioPasswordDialog />
        </main>
      </div>
    </div>
  );
}
